import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

// Le déverrouillage est tenu par le serveur (section_unlocks) : aucun drapeau
// dans le navigateur, qu'un utilisateur pourrait forcer depuis la console.
const isUnlocked = (user, section) => {
  const until = (user?.section_unlocks || {})[section];
  return !!until && new Date(until) > new Date();
};

export function SectionGate({ section, children }) {
  const { user, refreshUser } = useAuth();
  const locked = (user?.locked_sections || []).includes(section);
  const staffDenied = !!user?.acting_staff && !(user?.allowed_sections || []).includes(section);
  const [unlocked, setUnlocked] = useState(() => isUnlocked(user, section));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [recover, setRecover] = useState(false);
  const [mode, setMode] = useState("password"); // password | email
  const [acctPw, setAcctPw] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [newCode, setNewCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (staffDenied) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="section-denied">
        <Card className="p-8 rounded-md shadow-none max-w-sm w-full space-y-3 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <Lock className="h-6 w-6 text-destructive" />
          </div>
          <h2 className="text-lg font-semibold">Accès non autorisé</h2>
          <p className="text-sm text-muted-foreground">Ce module n'est pas activé pour votre compte personnel. Contactez votre gérant pour obtenir l'accès.</p>
        </Card>
      </div>
    );
  }

  if (!locked || unlocked) return children;

  const doUnlock = async () => {
    setUnlocked(true);
    setRecover(false);
    await refreshUser();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/security/section-lock/verify", { password, section });
      await doUnlock();
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const sendEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/security/section-lock/forgot", {});
      setEmailSent(true);
      toast.success(data.email_sent ? `Code envoyé à ${data.email}` : "Demande enregistrée (email non envoyé)");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const resetWithPassword = async () => {
    setBusy(true);
    try {
      await api.post("/security/section-lock/reset-with-password", { account_password: acctPw, new_code: newCode });
      toast.success("Nouveau code défini");
      await api.post("/security/section-lock/verify", { password: newCode, section });
      await doUnlock();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const resetWithEmail = async () => {
    setBusy(true);
    try {
      await api.post("/security/section-lock/reset-with-email", { email_code: emailCode, new_code: newCode });
      toast.success("Nouveau code défini");
      await api.post("/security/section-lock/verify", { password: newCode, section });
      await doUnlock();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-center py-16" data-testid="section-gate">
      <Card className="p-8 rounded-md shadow-none max-w-sm w-full space-y-4 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Lock className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Section protégée</h2>
          <p className="text-sm text-muted-foreground">Saisissez le code d'accès pour continuer.</p>
        </div>
        <form onSubmit={submit} className="space-y-3 text-left">
          <div>
            <Label className="text-xs">Code d'accès</Label>
            <Input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="section-gate-password" />
          </div>
          <Button type="submit" className="w-full" disabled={loading || !password} data-testid="section-gate-submit">
            {loading ? "Vérification…" : "Déverrouiller"}
          </Button>
        </form>
        <button type="button" onClick={() => setRecover(true)} className="text-xs text-primary hover:underline" data-testid="section-gate-forgot">
          Code oublié ?
        </button>
      </Card>

      <Dialog open={recover} onOpenChange={setRecover}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Récupérer le code des sections</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Réinitialiser le code d'accès aux sections protégées.</DialogDescription>
          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
            <button type="button" onClick={() => setMode("password")} className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded px-2 py-1.5 font-medium ${mode === "password" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="recover-mode-password">
              <KeyRound className="h-3.5 w-3.5" /> Mot de passe
            </button>
            <button type="button" onClick={() => setMode("email")} className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded px-2 py-1.5 font-medium ${mode === "email" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="recover-mode-email">
              <Mail className="h-3.5 w-3.5" /> Email
            </button>
          </div>

          {mode === "password" ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Mot de passe du compte</Label>
                <Input type="password" value={acctPw} onChange={(e) => setAcctPw(e.target.value)} className="mt-1" data-testid="recover-account-password" />
              </div>
              <div>
                <Label className="text-xs">Nouveau code d'accès (min. 4)</Label>
                <Input type="password" value={newCode} onChange={(e) => setNewCode(e.target.value)} className="mt-1" data-testid="recover-new-code-pw" />
              </div>
              <Button className="w-full" onClick={resetWithPassword} disabled={busy || !acctPw || newCode.length < 4} data-testid="recover-submit-password">Définir le nouveau code</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {!emailSent ? (
                <Button className="w-full" onClick={sendEmail} disabled={busy} data-testid="recover-send-email">
                  {busy ? "Envoi…" : "Recevoir un code par email"}
                </Button>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">Code reçu par email</Label>
                    <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value)} className="mt-1" data-testid="recover-email-code" placeholder="000000" />
                  </div>
                  <div>
                    <Label className="text-xs">Nouveau code d'accès (min. 4)</Label>
                    <Input type="password" value={newCode} onChange={(e) => setNewCode(e.target.value)} className="mt-1" data-testid="recover-new-code-email" />
                  </div>
                  <Button className="w-full" onClick={resetWithEmail} disabled={busy || !emailCode || newCode.length < 4} data-testid="recover-submit-email">Définir le nouveau code</Button>
                  <button type="button" onClick={sendEmail} disabled={busy} className="text-xs text-primary hover:underline w-full">Renvoyer un code</button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
